import {
  DesignationHeaderCell,
  ProductComparisonHeaderCell,
  TechValHeaderCell,
  WeightHeaderCell,
  AvailabilityHeaderCell,
} from "./ProductTabularViewHeaderCell";
import { type TabularColumnConfig, type TabularViewData } from "../../../../models/models";
import { ProductTabularViewActionCell } from "./ProductTabularViewActionCell/ProductTabularViewActionCell";
import { MAX_COLUMNS_IN_TABULAR_VIEW } from "../../../../../../utils/constants";
import { PopperTooltip } from "../../../../../../common/components/Tooltip";
import { useDispatch } from "react-redux";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import {
  useAddOrRemoveProductFromComparisonMutation,
  useProductComparisonMembership,
} from "~/features/compareProducts/api/productComparisonQuery";
import Checkbox from "../../../../../../common/components/Checkbox";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Link, LinkType } from "../../../../../../common/components/Link";
import ProductPreferredIndicator from "~/features/productDetails/components/ProductPreferredIndicator";
import { type ColumnDef } from "@tanstack/react-table";
import { HousingProductTabularViewActionCell } from "~/features/selectionAssistant/housing/components/HousingTabularView/HousingProductTabularViewActionCell";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { getDesignationAbbreviation } from "~/domains/product/utils/designationAbbreviation";
import { useTranslation } from "react-i18next";
import { type ProductData } from "~/domains/product/types/ProductData";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { getProductCampaignStatus } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { styled } from "~/common/mui";

import { renderTechValue } from "~/utils/renderTechValue";
import { Product } from "~/domains/product/Product";
import { ProductContext } from "~/domains/product/ProductContext";
import { ACTIONS_COL_WIDTH } from "./tabularViewShared";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

export { ACTIONS_COL_WIDTH, calculateTotalTableWidth } from "./tabularViewShared";

interface RenderDesignationCellProps {
  product: ProductData;
}

export const renderDesignationCell =
  (canClick?: boolean, maxChars?: number) =>
  ({ product: data }: RenderDesignationCellProps) => {
    const abbreviatedDesignation = getDesignationAbbreviation(maxChars)(data.designation);
    const isAbbreviation = abbreviatedDesignation !== data.designation;
    const { hasSale, isSoldOut } = getProductCampaignStatus(data.salesCampaign);
    let designation = (
      <ProductDesignation
        designation={abbreviatedDesignation}
        size={"label-medium"}
        className={isAbbreviation ? "f-no-wrap" : undefined}
        title={isAbbreviation ? data.designation : undefined}
        noCssColor={true}
      />
    );

    if (canClick && data.url) {
      designation = (
        <Link href={data.url} variant={LinkType.NAVIGATION} target={"_blank"}>
          {designation}
        </Link>
      );
    }

    return (
      <div className={"d-flex"}>
        <PreferredIndicator
          className={`icon-margin ${!data.preferred ? "v-hidden" : ""} scha__tabular-view__preferred-indicator`}
          side={"right"}
        />
        {designation}

        {hasSale && <SaleTag variant={isSoldOut ? "inactive" : "active"} size={20} className={"ml-auto flex-none"} />}
      </div>
    );
  };
export const tsmakeProductColumn = (isDesktop: boolean): ColumnDef<TabularViewData> => {
  return {
    id: "name",
    header: () => <DesignationHeaderCell />,
    size: isDesktop ? 180 : 138,
    cell: (cell) => renderDesignationCell(true, 120)(cell.row.original),
  };
};

const PreferredIndicator = styled(ProductPreferredIndicator)({
  lineHeight: 1,
});

const ComparisonTabularCheckbox = styled(Checkbox)({
  textAlign: "center",
  height: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const tsmakeHSAProductColumn = (isDesktop: boolean): ColumnDef<TabularViewData> => {
  return {
    id: "name",
    header: () => <DesignationHeaderCell />,
    size: isDesktop ? 240 : 120,
    cell: (cell) => renderDesignationCell(cell.row.original.variantProducts.length === 0, 12)(cell.row.original),
    // cellStyle: {
    //   paddingLeft: 0,
    //   paddingRight: 10,
    // },
    // sorting: true,
  };
};

export const dataColSizeFn = (isDesktop: boolean, maxColumnAmount: number) => (id: string) => {
  if (!isDesktop) {
    return 85;
  }
  if (maxColumnAmount <= MAX_COLUMNS_IN_TABULAR_VIEW) {
    return undefined;
  }
  return ID_SIZE_MAP[id] ?? 105;
};

const ID_SIZE_MAP: Record<string, number> = {
  limitingspeedgrease: 160,
  limitingspeedoil: 135,
  limitingspeed: 125,
  referencespeed: 125,
  tempmin: 110,
  tempmax: 110,
};

const renderTechValueCell = (col: TabularColumnConfig) => (data: TabularViewData) => {
  const { abbreviation, value = "-", isConverted } = data.techValues[col.techValueUID] ?? {};

  const isInch = col.unit?.toLowerCase() == "in";

  const label = renderTechValue(value, isInch, isConverted);

  if (abbreviation) {
    const abbreviationCmp = <span dangerouslySetInnerHTML={{ __html: abbreviation + " " + col.unit }} />;

    return (
      <PopperTooltip
        tooltipContent={<span className={"f-no-wrap"}>{abbreviationCmp}</span>}
        className={isConverted ? "d-inline f-info-color" : "d-inline"}
      >
        {label}
      </PopperTooltip>
    );
  }

  return label;
};
export const ComparisonCell = ({ data }: { data: TabularViewData }) => {
  const dispatch = useDispatch();
  const { isInComparison: isChecked } = useProductComparisonMembership(data.product.code);
  const comparisonMutation = useAddOrRemoveProductFromComparisonMutation();
  const loggedIn = userIsLoggedIn();
  const handleAddToComparison = () => {
    if (loggedIn) {
      comparisonMutation.mutate(
        { productCode: data.product.code, isInComparison: isChecked },
        { onError: (error) => console.error("Failed to update product comparison", error) },
      );
      return;
    }
    dispatch(openAuthOverlay("login"));
  };
  return (
    <>
      <ComparisonTabularCheckbox
        onCheck={handleAddToComparison}
        checked={isChecked}
        className={"scha__comparison__tabular "}
      />
    </>
  );
};
export const WeightCell = ({ data }: { data: TabularViewData }) => {
  const { t } = useTranslation();
  const unitMode = useUnitMode();
  const valueWeight = unitMode === UnitMode.Metric ? data.weight : data.weightImperial;

  const label = <span>{valueWeight}</span>;

  const abbreviationCmp =
    unitMode === UnitMode.Metric
      ? t("page.selectionAssistant.tableHeader.mass.label")
      : t("page.selectionAssistant.tableHeader.mass.imperial.label");

  return (
    <PopperTooltip
      tooltipContent={<span className={"f-no-wrap"}>{abbreviationCmp}</span>}
      className={unitMode === UnitMode.Imperial && valueWeight !== "-" ? "d-inline f-info-color" : "d-inline"}
    >
      {label}
    </PopperTooltip>
  );
};
export const tsmakeTechValueColumnFactory =
  (sizeCalculator: (code: string) => number | undefined) =>
  (colDef: TabularColumnConfig): ColumnDef<TabularViewData> => {
    return {
      id: colDef.techValueUID,
      header: () => <TechValHeaderCell config={colDef} alignment={"right"} />,
      size: sizeCalculator(colDef.techValueUID),
      cell: (cell) => renderTechValueCell(colDef)(cell.row.original),
      meta: {
        align: "right",
      },
    };
  };

export const tsmakeWeightColumn = (
  sizeCalculator: (code: string) => number | undefined,
): ColumnDef<TabularViewData> => {
  return {
    id: "weight",
    header: () => <WeightHeaderCell />,
    size: sizeCalculator("weight"),
    cell: (cell) => <WeightCell data={cell.row.original} />,
    meta: {
      align: "right",
    },
  };
};
export const tsmakeWeightColumnImperial = (
  sizeCalculator: (code: string) => number | undefined,
): ColumnDef<TabularViewData> => {
  return {
    id: "weightImperial",
    header: () => <WeightHeaderCell />,
    size: sizeCalculator("weightImperial"),
    cell: (cell) => <WeightCell data={cell.row.original} />,
    meta: {
      align: "right",
    },
  };
};

export const tsmakeCompareColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "compare",
    header: () => <ProductComparisonHeaderCell />,
    cell: (cell) => <ComparisonCell data={cell.row.original} />,
    size: 106,
    meta: {},
  };
};

export const tsmakeActionsColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "actions",
    header: "",
    cell: (cell) => <ProductTabularViewActionCell productCode={cell.row.original.product.code} />,
    size: ACTIONS_COL_WIDTH,
    meta: {
      columnType: "action",
      className: "scha__tabular-view__action-cell-bg",
      align: "right",
    },
  };
};
export const tsmakeHSAActionsColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "actions",
    header: "",
    cell: (cell) => {
      return <HousingProductTabularViewActionCell row={cell.row} />;
    },
    size: ACTIONS_COL_WIDTH,
    meta: {
      columnType: "action",
      align: "right",
      getBodyCellClasses: (row) =>
        row.original.variantProducts.length > 0
          ? "scha__tabular-view__hsa-action-cell-bg"
          : "scha__tabular-view__action-cell-bg",
    },
  };
};

export const tsmakeAvailabilityColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "availability",
    header: () => <AvailabilityHeaderCell />,
    size: 170,
    cell: (cell) => {
      const product = cell.row.original.product;
      return (
        <ProductContext value={product}>
          <Product.StockIndicator className="text-nowrap" />
        </ProductContext>
      );
    },
  };
};
